import {type _ActionsTree} from 'pinia';
import {type LearningStoreState} from "@/store/learning/state";
import {type LearningStoreGetters} from "@/store/learning/getters";
import {LearningMenuType} from "@/components/Learning/LearningMiddle/config/LearningMenuType";
import {LearningCourseModel} from "~~/package/mapper/learning/model/LearningCourseModel";
import {TaskModel} from "~~/package/mapper/learning/model/TaskModel";
import {LearningSkillModel} from "~~/package/mapper/learning/model/LearningSkillModel";
import {LinuxInstructionModel} from "~~/package/mapper/learning/model/linux/LinuxInstructionModel";
import {LinuxLearningModel} from "~~/package/mapper/learning/model/linux/LinuxLearningModel";
import type TaskCompleteResponse from "~~/package/apis/learning/response/TaskCompleteResponse";
import {LevelAssessmentModel} from "~~/package/mapper/levelAssessment/model/LevelAssessmentModel";
import {
    ProSkillAssureAssessmentModel
} from "~~/package/mapper/proSkillAssure/model/assessment/ProSkillAssureAssessmentModel";
import {
    ProSkillAssureAssessmentSetModel
} from "~~/package/mapper/proSkillAssure/model/assessment/ProSkillAssureAssessmentSetModel";
import {
    ProSkillAssureAssessmentMyInfoModel
} from "~~/package/mapper/proSkillAssure/model/assessment/myInfo/ProSkillAssureAssessmentMyInfoModel";
import type {FileNode} from "@webcontainer/api/dist/entities";
import type FileData from "~~/package/types/FileData";
import {LearningTokenData} from "~~/package/mapper/learning/model/LearningTokenData";
import { basicActions } from "@/store/learning/actions/basicActions";
import { navigationActions } from "@/store/learning/actions/navigationActions";
import { resetActions } from "@/store/learning/actions/resetActions";
import { domainActions } from "@/store/learning/actions/domainActions";
import { directoryActions } from "@/store/learning/actions/directoryActions";
import { assessmentActions } from "@/store/learning/assessment/actions";

export interface LearningStoreActions extends _ActionsTree {
    changeThemeMode: () => void;
    resetInitData: () => void;
    resetSubmissionFlags: (taskId?: number, preserveCurrentChanges?: boolean) => void;
    updateMenuType: (menuType: LearningMenuType, isAutoClose?: boolean) => void;
    updateCode: (code: string) => void;
    updateCourse: (course: LearningCourseModel) => void;
    updateLearningData: (task: TaskModel) => void;
    updateToken: (tokenData: LearningTokenData) => void;
    updateProgress: () => void;
    runTraining: (isPreview?: boolean) => void;
    addTrainingData: (codeOutPut: string, imageDataList: string[], errorOutPut: string, errorGuide: string, isEnd: boolean, validSet: string, score: number | undefined) => void;
    updateIsShowImage: (isShow: boolean) => void;
    resetCodeData: () => void;
    getSkill: (skillId: number) => LearningSkillModel;
    getSkillIndex: (skillId: number) => number;
    getTaskId: (taskId: number, isNext: boolean) => number;
    handlingTaskSuccess: () => void;
    resetLearningMessage: () => void;
    getNextAssessmentId: (taskId: number) => number;
    resetMultiFileLearning: () => void;
    resetAssessment: () => void;
    resetProSkillAssure: () => void;
    resetAll: () => void;
    resetContestData: () => void;
    updateContestList: (isSubmitted?: boolean) => void;
    updateLinuxInstruction: (linuxLearningData: LinuxLearningModel<LinuxInstructionModel[]>) => void;
    completeLinuxInstruction: (linuxInstruction: LinuxInstructionModel) => void;
    allCompleteLinuxInstruction: () => void;
    handleTaskComplete: (taskCompleteData: TaskCompleteResponse) => void;
    updateTrackLevelInfo: (trackInfo: LevelAssessmentModel, levelId: string) => void;
    resetLevelAssessment: () => void;
    getNextLevelAssessmentId: (taskTemplateId: number) => number;
    updateProSkillAssessmentInfo: (id: number, assessment: ProSkillAssureAssessmentModel, myInfo: ProSkillAssureAssessmentMyInfoModel, remainingTime?: number) => void;
    getCurrentProSkillAssureSet: (taskId: number) => ProSkillAssureAssessmentSetModel;
    getCurrentProSkillAssureSetIndex: (skillId: number) => number;
    addSelectedDirectoryFile: (path: string, key: string, file: FileNode) => void;
    setFileData: (file: FileData, isActive: boolean) => FileData;
    updateSelectedDirectoryFile: (fileData: {path: string, contents: string | Uint8Array} | null) => void;
    removeSelectedDirectoryFile: (key: string, index: number) => void;
    activeSelectedDirectoryFile: (index: number) => void;
    handleTaskValidate: (taskCompleteData: TaskCompleteResponse) => void;
    refreshPreview: () => void;
    addModifiedFiles: (file: FileData) => void;
    updateCustomFunctions: (functions: string[], path: string) => void;
    clearCustomFunctions: () => void;
    updateSplitPaneSize: (leftSize: number, rightSize: number) => void;
}

export const actions: LearningStoreActions | LearningStoreState | LearningStoreGetters = {
    ...assessmentActions,
    ...basicActions,
    ...navigationActions,
    ...resetActions,
    ...domainActions,
    ...directoryActions
};
